/**
 * Role template builder for the Setup Wizard.
 *
 * Derives permission menus from the permission catalogue LiftCloud serves
 * (GET /api/permission-catalogue/, every flag on) by selectively disabling
 * actions based on the chosen template tier. The catalogue is a parameter, not
 * an import: the caller fetches it, and a test can hand in any tree.
 *
 * Templates:
 *  VIEW_ONLY     – Can view dashboards, reports, and edge config. No write access.
 *  VIEW_CONTROL  – VIEW_ONLY + can send monitor commands.
 *  ADMIN         – Full access except org CRUD and root-permissions.
 *  MASTER_ADMIN  – Unrestricted (the catalogue as served).
 *
 * root-permissions is master-admin-only: ADMIN, VIEW_CONTROL and VIEW_ONLY all
 * disable every action under that sub-page. It edits the global permission
 * catalogue, so a tier below ADMIN must not reach what ADMIN itself is denied.
 *
 * Note "every action under it", not "the sub-page": `disableAction` only clears
 * the flags on actions, so the sub-page NODE keeps active/offline true and
 * `flattenPermissions` still emits its bare `root-permissions` tree_key for
 * these tiers. Nothing gates on that key today — Layout2's `rootSubmenuKeys` is
 * an antd open-keys list, not a permission check — but a future gate reading the
 * page key rather than an action key would not be covered by this.
 */
import { Permission } from "../../../types/permissions";
import type { RoleMenuJson } from "../utils/roleMenu";

export type RoleTemplate = "VIEW_ONLY" | "VIEW_CONTROL" | "ADMIN" | "MASTER_ADMIN";

export const ROLE_TEMPLATES: {
  value: RoleTemplate;
  label: string;
  description: string;
}[] = [
  {
    value: "VIEW_ONLY",
    label: "View Only",
    description: "Can view dashboards, reports, and edge configuration. No write access anywhere.",
  },
  {
    value: "VIEW_CONTROL",
    label: "View + Control",
    description: "View Only plus the ability to send monitor commands (actions).",
  },
  {
    value: "ADMIN",
    label: "Admin",
    description:
      "Full read/write access to edge management, users, roles, reports, and tools. Cannot create or delete organizations.",
  },
  {
    value: "MASTER_ADMIN",
    label: "Master Admin",
    description: "Unrestricted access to everything including organization management.",
  },
];

function disableAction(act: any) {
  Object.assign(act, { active: false, offline: false });
}

// The root-permissions sub-page edits the GLOBAL permission catalogue, not
// anything scoped to the role's own organization. ADMIN already denies it, so
// the two strictly-lower tiers must not be the ones that grant it:
// `shouldKeepViewOnly` keeps every action whose verb is "view", which would
// otherwise hand VIEW_ONLY and VIEW_CONTROL read of a catalogue ADMIN itself
// does not get.
//
// This was inert while the catalogue's uris lacked the `/api` prefix and could
// never match `sanitizeURI`'s output. They match now, so the hole is live.
//
// Matched on sub-page name alone, deliberately. Adding a `page.name === "tools"`
// guard would read as tighter but is strictly worse: every tier has to agree on
// what root-permissions IS, and if a second sub-page of that name ever appeared
// under another page, a tools-scoped predicate would deny it to ADMIN (whose
// check is unscoped) while granting it to the two tiers below — the exact
// inversion this exists to prevent. All three tiers now route through here.
function isRootPermissions(subPage: any): boolean {
  return subPage.name === "root-permissions";
}

function shouldKeepViewOnly(page: any, act: any): boolean {
  if (act.name === "view") return true;
  if (page.name === "reports") return true;
  return false;
}

/**
 * VIEW_CONTROL adds exactly one grant to VIEW_ONLY: the monitor command.
 *
 * Keyed on the action's tree_key rather than on (page, verb). The broader rule
 * -- any `create` under monitor-dashboard -- also caught MonitorGridCreate
 * (POST /api/monitor-grid/), which is not a command at all: it persists a
 * monitor grid layout, and is what MonitoringHeader gates `canSaveGrid` on.
 * That is configuration, and this tier's own description promises the operator
 * can "operate lifts through the monitoring control panel, but not change
 * configuration". The verb-shaped rule could not tell the two apart because
 * both are spelled `create` on the same sub_page; the identity can.
 */
function shouldKeepViewControl(page: any, act: any): boolean {
  if (shouldKeepViewOnly(page, act)) return true;
  return act.tree_key === Permission.MonitorGridCommandCreate;
}

/**
 * Apply the template's rules to every action `container` owns, then recurse
 * into its sub_pages.
 *
 * The recursion is load-bearing, not stylistic. Every node in the catalogue
 * menu ships `active: true`, and templates subtract grants — so a node
 * this walk fails to reach keeps its grant, and the role is stored that way.
 * A fixed page > sub_page walk therefore silently grants any action declared
 * directly on a top-level page, or nested one level deeper than it looks for.
 *
 * `page` is the top-level page the action ultimately hangs off, which is what
 * `shouldKeepViewOnly` / `shouldKeepViewControl` are written in terms of.
 * `container` is the node that directly owns the action — the page itself at
 * the top level, a sub_page below it. ADMIN's rules key on the container,
 * which is the same node the two-level walk called `subPage` at depth two.
 * Note that the container name is matched unqualified at EVERY depth now, so a
 * future node named "organizations" or "root-permissions" anywhere in the tree
 * would be revoked for ADMIN; no such node exists outside depth two today.
 *
 * All three tiers route their root-permissions decision through
 * `isRootPermissions(container)`. Keeping that check on VIEW_ONLY and
 * VIEW_CONTROL is the whole point of having it: without it, the blanket "keep
 * every view verb" rule hands the two tiers below ADMIN a `view` on the global
 * permission catalogue that ADMIN itself is denied — the privilege inversion
 * the predicate exists to prevent. The recursion and this check arrived on
 * separate branches, and taking the recursion alone would have silently
 * dropped it.
 */
function applyToContainer(template: RoleTemplate, page: any, container: any) {
  (container.actions || []).forEach((act: any) => {
    switch (template) {
      // Both tiers below ADMIN share the identical organization-management /
      // root-permissions exclusion — one shared case, checked once, so a future
      // edit to that exclusion cannot land on only one of the two tiers and
      // silently reopen the privilege inversion the comment above documents.
      case "VIEW_ONLY":
      case "VIEW_CONTROL":
        if (page.name === "organization-management" || isRootPermissions(container)) {
          disableAction(act);
          return;
        }
        if (template === "VIEW_ONLY") {
          if (!shouldKeepViewOnly(page, act)) disableAction(act);
        } else if (!shouldKeepViewControl(page, act)) {
          disableAction(act);
        }
        break;

      case "ADMIN":
        if (container.name === "organizations" && act.name !== "view") {
          disableAction(act);
          return;
        }
        if (isRootPermissions(container)) disableAction(act);
        break;

      default:
        break;
    }
  });

  (container.sub_pages || []).forEach((child: any) => applyToContainer(template, page, child));
}

function applyTemplate(template: RoleTemplate, menu: any) {
  (menu.pages || []).forEach((page: any) => applyToContainer(template, page, page));
}

/**
 * Build a role menu JSON object for the given template from `catalogueMenu`,
 * the permission catalogue's menu (every flag on).
 *
 * Works on a copy: the catalogue is shared (RTK Query's cache) and is never
 * mutated.
 */
export function buildRoleMenu(template: RoleTemplate, catalogueMenu: RoleMenuJson): any {
  // JSON round-trip: the catalogue is plain JSON off the wire, so this is a
  // complete deep copy, and it needs no structuredClone (absent in jest 27).
  const menu = JSON.parse(JSON.stringify(catalogueMenu));

  if (template === "MASTER_ADMIN") {
    return menu;
  }

  applyTemplate(template, menu);

  // For VIEW_ONLY / VIEW_CONTROL, also mark org-management page as inactive
  if (template === "VIEW_ONLY" || template === "VIEW_CONTROL") {
    const orgPage = (menu.pages || []).find((p: any) => p.name === "organization-management");
    if (orgPage) {
      Object.assign(orgPage, { active: false, offline: false });
    }
  }

  return menu;
}
